<template>
  <div class="w-full h-full flex flex-col">
    <div class="flex flex-col gap-3 mb-3">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div class="text-lg lg:text-2xl font-bold">
            {{ title }}
            <button v-if="type === 'Contracts'" class="mx-2 text-sm lg:text-md" @click="showAllContractFilter">Alle Anzeigen</button>
          </div>
          <p v-if="type === 'Contracts' && !filters.Status && !showAllContracts" class="text-xs font-normal text-gray-500 mt-1">
            Archivierte Projekte sowie Verträge mit Status „Widerrufen“ oder „Absage“ werden ausgeblendet.
          </p>
        </div>
      
      <div class="flex items-center gap-2 flex-wrap relative">
        <button class="px-3 py-2 border rounded text-sm lg:text-base" @click="addNewEntry">Neu</button>
        <input
          v-model="search"
          @input="debouncedReload()"
          type="text"
          placeholder="Suche..."
          class="border rounded px-3 py-2 text-sm lg:text-base w-full sm:w-auto"
        />
        <select v-if="type === 'Contracts'" v-model="filters.Status" @change="reload()" class="border rounded px-3 py-2 text-sm lg:text-base">
          <option :value="''">Relevante Status</option>
          <option v-for="o in statusOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <select v-if="type === 'Customers'" v-model="filters.CustomerStatus" @change="reload()" class="border rounded px-3 py-2 text-sm lg:text-base">
          <option :value="''">Alle Kunden-Status</option>
          <option v-for="o in customerStatusOptions" :key="'cs-'+o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <div v-if="type === 'Contracts'" class="relative">
          <input
            v-model="projectSearch"
            @focus="showProjectDropdown = true"
            @blur="hideProjectDropdown"
            @keydown.enter="selectFirstProject"
            type="text"
            :placeholder="filters.Projekt ? getProjectLabel(filters.Projekt) : 'Alle Projekte'"
            class="border rounded px-3 py-2 w-full sm:w-48 text-sm lg:text-base"
          />
          <button v-if="filters.Projekt" @click="clearProject" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <font-awesome-icon icon="times" />
          </button>
          <div v-if="showProjectDropdown && filteredProjectOptions.length > 0" class="absolute z-50 bg-white border rounded shadow-lg mt-1 max-h-48 overflow-y-auto w-full sm:w-48">
            <div
              v-for="opt in filteredProjectOptions"
              :key="opt.value"
              @mousedown.prevent="selectProject(opt.value)"
              class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
            >
              {{ opt.label }}
            </div>
          </div>
        </div>
        <div v-if="type==='Contracts'" class="flex items-center gap-2">
          <label class="text-sm text-gray-600">Stichtag</label>
          <input type="date" v-model="filters.TargetDate" @change="reload()" class="border rounded px-2 py-2 text-sm lg:text-base"/>
        </div>
        <button class="px-3 py-2 border rounded relative text-sm lg:text-base" @click="openColumnModal">
          <span class="hidden sm:inline">Spalten</span>
          <span class="sm:hidden">Cols</span>
          <span class="ml-2 inline-flex items-center justify-center text-xs font-semibold rounded-full bg-gray-200 px-2 py-0.5">
            {{ visibleCount }}
          </span>
        </button>
        <button class="hidden lg:block px-3 py-2 border rounded text-sm lg:text-base" @click="exportCsv" :disabled="loading">Export</button>
        <button v-if="type==='Contacts'" class="px-3 py-2 border rounded bg-green-100 text-green-800 hover:bg-green-200 text-sm lg:text-base" @click="openImportModal">Import CSV</button>
        <button v-if="hasActiveFilters" class="px-3 py-2 border rounded bg-red-100 text-red-800 hover:bg-red-200 text-sm lg:text-base" @click="clearAllFilters">Filter löschen</button>
        
        <!-- Infomail/Mail buttons for Contracts (desktop only, mobile uses Aktionen dropdown) -->
        <div v-if="type==='Contracts'" class="hidden lg:flex items-center gap-2 border-l pl-2 ml-2">
          <button @click="sendInfomail(1)" class="px-3 py-2 border rounded bg-blue-50 text-blue-800 hover:bg-blue-100 text-sm" title="Infomail an Eltern 1">Infomail E1</button>
          <button @click="sendInfomail(2)" class="px-3 py-2 border rounded bg-blue-50 text-blue-800 hover:bg-blue-100 text-sm" title="Infomail an Eltern 2">Infomail E2</button>
          <button @click="sendInfomail(3)" class="px-3 py-2 border rounded bg-blue-50 text-blue-800 hover:bg-blue-100 text-sm" title="Infomail an beide Eltern">Infomail E1&2</button>
          <button @click="sendMail(1)" class="px-3 py-2 border rounded bg-gray-50 text-gray-800 hover:bg-gray-100 text-sm" title="Mail an Eltern 1">Mail E1</button>
          <button @click="sendMail(2)" class="px-3 py-2 border rounded bg-gray-50 text-gray-800 hover:bg-gray-100 text-sm" title="Mail an Eltern 2">Mail E2</button>
          <button @click="sendMail(3)" class="px-3 py-2 border rounded bg-gray-50 text-gray-800 hover:bg-gray-100 text-sm" title="Mail an beide Eltern">Mail E1&2</button>
          <button @click="showEssensinfos" class="px-3 py-2 border rounded bg-orange-50 text-orange-800 hover:bg-orange-100 text-sm" title="Essensinfos anzeigen">Essensinfos</button>
        </div>

        <!-- Aktionen Dropdown (mobile only) -->
        <div class="relative lg:hidden">
          <button @click="aktionenOpen = !aktionenOpen" class="px-3 py-2 border rounded text-sm bg-gray-50 hover:bg-gray-100 flex items-center gap-1">
            Aktionen
            <font-awesome-icon :icon="aktionenOpen ? 'chevron-up' : 'chevron-down'" class="text-xs" />
          </button>
          <div v-if="aktionenOpen" class="absolute right-0 top-full mt-1 bg-white border rounded shadow-lg z-50 w-56 max-h-[70vh] overflow-y-auto">
            <template v-if="type==='Contracts'">
              <button @click="showEssensinfos(); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-orange-50 border-b">Essensinfos</button>
              <div class="px-4 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-100 border-b">Infomails</div>
              <button @click="sendInfomail(1); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-blue-50">Infomail E1</button>
              <button @click="sendInfomail(2); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-blue-50">Infomail E2</button>
              <button @click="sendInfomail(3); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-blue-50">Infomail E1&2</button>
              <div class="px-4 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-100 border-b">Mails</div>
              <button @click="sendMail(1); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">Mail E1</button>
              <button @click="sendMail(2); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">Mail E2</button>
              <button @click="sendMail(3); aktionenOpen = false" class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">Mail E1&2</button>
            </template>
            <button @click="exportCsv(); aktionenOpen = false" :disabled="loading" class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-t">
              Export
            </button>
          </div>
        </div>
       
        <a v-if="type==='Projects'" :href="legacyProjectsOverviewUrl" target="_blank" class="px-3 py-2 border rounded bg-gray-50 hover:bg-gray-100 text-sm lg:text-base">
          <span class="hidden sm:inline">Projektübersicht (Alt)</span>
          <span class="sm:hidden">Übersicht (Alt)</span>
        </a>
        <!-- Column Selection Modal -->
        <div v-if="columnModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" @click="closeColumnModal">
          <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden m-4" @click.stop>
            <div class="flex justify-between items-center p-4 lg:p-6 border-b">
              <h2 class="text-lg lg:text-xl font-semibold">Spaltenkonfiguration</h2>
              <button @click="closeColumnModal" class="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
            </div>
            
            <div class="p-4 lg:p-6">
              <div class="mb-4">
                <input 
                  v-model="columnFilter" 
                  type="text" 
                  placeholder="Spalten filtern..." 
                  class="w-full border rounded px-3 py-2 text-sm lg:text-base"
                />
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                <!-- Visible Columns (Drag & Drop Reordering) -->
                <div>
                  <h3 class="font-semibold mb-3 text-gray-700">Sichtbare Spalten (Reihenfolge anpassen)</h3>
                  <div class="border rounded-lg p-3 min-h-[300px] max-h-[400px] overflow-y-auto bg-gray-50">
                    <div 
                      v-for="(col, index) in visibleColumnsList" 
                      :key="col.key"
                      draggable="true"
                      @dragstart="handleDragStart(col, 'visible', index)"
                      @dragover.prevent
                      @drop="handleDrop(col, 'visible', index)"
                      class="mb-2 p-2 bg-white border rounded cursor-move hover:shadow-md transition-shadow"
                    >
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                          <span class="text-gray-400 cursor-move">☰</span>
                          <span class="font-medium">{{ col.label }}</span>
                        </div>
                        <button 
                          @click="toggleColumn(col.key, false)"
                          class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                        >
                          ausblenden
                        </button>
                      </div>
                    </div>
                    <div v-if="visibleColumnsList.length === 0" class="text-gray-500 text-center py-4">
                      Keine sichtbaren Spalten
                    </div>
                  </div>
                </div>
                
                <!-- Available Columns -->
                <div>
                  <h3 class="font-semibold mb-3 text-gray-700">Verfügbare Spalten</h3>
                  <div class="border rounded-lg p-3 min-h-[300px] max-h-[400px] overflow-y-auto bg-gray-50">
                    <div 
                      v-for="col in availableColumns" 
                      :key="col.key"
                      draggable="true"
                      @dragstart="handleDragStart(col, 'available')"
                      @dragover.prevent
                      @drop="handleDrop(col, 'available')"
                      class="mb-2 p-2 bg-white border rounded cursor-move hover:shadow-md transition-shadow"
                      :class="{ 'opacity-50': !visible[col.key] }"
                    >
                      <div class="flex items-center justify-between">
                        <span class="font-medium">{{ col.label }}</span>
                        <button 
                          @click="toggleColumn(col.key, !visible[col.key])"
                          class="px-2 py-1 text-xs rounded"
                          :class="visible[col.key] ? 'bg-red-100 text-red-800 hover:bg-red-200' : 'bg-green-100 text-green-800 hover:bg-green-200'"
                        >
                          {{ visible[col.key] ? 'ausblenden' : 'einblenden' }}
                        </button>
                      </div>
                    </div>
                    <div v-if="availableColumns.length === 0" class="text-gray-500 text-center py-4">
                      Keine Spalten gefunden
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-4 lg:p-6 border-t bg-gray-50">
              <div class="flex gap-2">
                <button class="px-4 py-2 border rounded text-sm bg-green-100 text-green-800 hover:bg-green-200" @click="showAllCols(true)">Alle einblenden</button>
                <button class="px-4 py-2 border rounded text-sm bg-red-100 text-red-800 hover:bg-red-200" @click="showAllCols(false)">Alle ausblenden</button>
                <button class="px-4 py-2 border rounded text-sm hover:bg-gray-100" @click="resetCols()">Zurücksetzen</button>
              </div>
              <button class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700" @click="closeColumnModal">
                Schließen
              </button>
            </div>
          </div>
        </div>

        <!-- Essensinfos Modal -->
        <div v-if="essensinfosModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" @click="closeEssensinfosModal">
          <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden m-4" @click.stop>
            <div class="flex justify-between items-center p-4 lg:p-6 border-b">
              <h2 class="text-lg lg:text-xl font-semibold">Essensinfos</h2>
              <button @click="closeEssensinfosModal" class="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
            </div>
            
            <div class="p-4 lg:p-6 overflow-y-auto">
              <table class="w-full border-collapse text-sm lg:text-base">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="border px-4 py-2 text-left">Kombination</th>
                    <th class="border px-4 py-2">Montag</th>
                    <th class="border px-4 py-2">Dienstag</th>
                    <th class="border px-4 py-2">Mittwoch</th>
                    <th class="border px-4 py-2">Donnerstag</th>
                    <th class="border px-4 py-2">Freitag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(count, combi) in essensinfosData" :key="combi">
                    <td class="border px-4 py-2 font-medium">{{ combi }}</td>
                    <td class="border px-4 py-2 text-center">{{ count.Montag || 0 }}</td>
                    <td class="border px-4 py-2 text-center">{{ count.Dienstag || 0 }}</td>
                    <td class="border px-4 py-2 text-center">{{ count.Mittwoch || 0 }}</td>
                    <td class="border px-4 py-2 text-center">{{ count.Donnerstag || 0 }}</td>
                    <td class="border px-4 py-2 text-center">{{ count.Freitag || 0 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Import Modal for Contacts -->
        <div v-if="importModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" @click="closeImportModal">
          <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4" @click.stop>
            <div class="flex justify-between items-center p-4 lg:p-6 border-b">
              <h2 class="text-lg lg:text-xl font-semibold">CSV Import</h2>
              <button @click="closeImportModal" class="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
            </div>
            
            <div class="p-4 lg:p-6">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">CSV-Datei auswählen</label>
                <input 
                  type="file" 
                  accept=".csv"
                  @change="onFileSelected"
                  class="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div class="mb-4 text-sm text-gray-600">
                <p class="font-semibold mb-2">Hinweise:</p>
                <ul class="list-disc list-inside space-y-1">
                  <li>Die CSV-Datei sollte UTF-8 kodiert sein</li>
                  <li>Erste Zeile muss Spaltenüberschriften enthalten</li>
                  <li>Kontakte werden anhand von Email_1 oder Name identifiziert</li>
                  <li>Existierende Kontakte werden aktualisiert, neue werden erstellt</li>
                </ul>
              </div>

              <div class="mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <p class="font-semibold mb-2">Erwartete Spalten:</p>
                <p class="font-mono text-xs">Anrede, Nachname, Vorname, Unternehmen, Strasse, Hausnummer, Postleitzahl, Ort, Festnetz, Mobilnummer, Email_1, Email_2, Fax, Funktion, Weihnachtskarte, Geburtsdatum, Sonstiges</p>
              </div>

              <div v-if="importResult" class="mb-4 p-4 rounded" :class="importResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'">
                <p class="font-semibold mb-2">{{ importResult.success ? 'Import erfolgreich!' : 'Import fehlgeschlagen' }}</p>
                <p v-if="importResult.created !== undefined">Erstellt: {{ importResult.created }}</p>
                <p v-if="importResult.updated !== undefined">Aktualisiert: {{ importResult.updated }}</p>
                <div v-if="importResult.errors && importResult.errors.length > 0">
                  <p class="font-semibold mt-2">Fehler:</p>
                  <ul class="list-disc list-inside max-h-40 overflow-y-auto">
                    <li v-for="(error, idx) in importResult.errors" :key="idx" class="text-xs">{{ error }}</li>
                  </ul>
                </div>
                <p v-if="importResult.error" class="text-sm">{{ importResult.error }}</p>
              </div>
            </div>
            
            <div class="flex flex-col sm:flex-row justify-end gap-2 p-4 lg:p-6 border-t bg-gray-50">
              <button class="px-4 py-2 border rounded hover:bg-gray-100" @click="closeImportModal" :disabled="importing">
                Schließen
              </button>
              <button 
                class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" 
                @click="performImport"
                :disabled="!importFile || importing"
              >
                {{ importing ? 'Importiere...' : 'Importieren' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Summary of active filters -->
    <div v-if="activeFilterChips.length" class="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-blue-100 bg-blue-50/40 px-3 py-2">
      <span class="mr-1 text-xs font-bold uppercase tracking-wide text-gray-500">Aktive Filter</span>
      <span v-for="chip in activeFilterChips" :key="chip.kind + '-' + chip.key"
        class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1 text-sm text-blue-900 shadow-sm">
        <span class="font-semibold">{{ chip.label }}:</span>
        <span class="truncate">{{ chip.display }}</span>
        <button
          @click="removeFilterChip(chip)"
          class="!m-0 !flex !h-6 !w-6 !shrink-0 !items-center !justify-center !rounded-full !border-0 !bg-transparent !p-0 !text-blue-500 !shadow-none hover:!bg-blue-100 hover:!text-blue-700 focus:!outline-none focus:!ring-2 focus:!ring-blue-200"
          title="Filter entfernen"
          :aria-label="`Filter ${chip.label} entfernen`"
        >
          <font-awesome-icon icon="times" class="text-xs" />
        </button>
      </span>
    </div>

    <div
      ref="scrollContainer"
      class="flex-1 overflow-auto border rounded bg-white"
      @scroll.passive="onScroll"
    >
      <table class="w-full text-left border-collapse min-w-max">
        <thead class="sticky top-0 z-20">
          <tr>
            <th class="p-2 bg-gray-50 border-b border-gray-200 w-10 sticky left-0 z-30">Aktion</th>
            <th v-for="col in visibleColumnsList" :key="col.key" class="p-2 bg-gray-50 border-b border-gray-200" :style="{ width: col.width || 'auto' }">
              <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold uppercase text-gray-600 truncate" :title="col.label">{{ col.label }}</span>
                  <button v-if="col.sortable" @click="toggleSort(col.key)" class="text-gray-400 hover:text-primary ml-1">
                    <font-awesome-icon :icon="getSortIcon(col.key)" />
                  </button>
                </div>
                
                <div v-if="col.filterable" class="relative group">
                  <!-- Clear button -->
                  <button 
                    v-if="hasFilter(col.key)" 
                    @click="clearFilter(col.key)" 
                    class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Filter löschen"
                  >
                    <font-awesome-icon icon="times" />
                  </button>

                  <div v-if="type==='Customers' && col.key==='Status'">
                    <select v-model="filters.CustomerStatus" @change="reload()" class="border rounded px-2 py-1 w-full text-xs">
                      <option :value="''">Alle</option>
                      <option v-for="o in customerStatusOptions" :key="'cs2-'+o.value" :value="o.value">{{ o.label }}</option>
                    </select>
                  </div>

                  <div v-else-if="col.type === 'boolean'">
                    <select v-model="filters[col.key]" @change="reload()" class="border rounded px-2 py-1 w-full text-xs">
                      <option value="">Alle</option>
                      <option value="True">Ja</option>
                      <option value="False">Nein</option>
                    </select>
                  </div>

                  <div v-else-if="col.type === 'date'" class="flex flex-col gap-1">
                    <select v-model="operators[col.key]" class="text-[10px] border rounded bg-gray-50 px-1 py-0.5" @change="reload()">
                      <option value="eq">=</option>
                      <option value="gt">&gt;</option>
                      <option value="lt">&lt;</option>
                      <option value="gte">&ge;</option>
                      <option value="lte">&le;</option>
                    </select>
                    <input v-model="dateFilters[col.key]" @change="reload()" type="date" class="border rounded px-1 py-0.5 w-full text-xs" />
                  </div>

                  <div v-else class="relative">
                    <input v-model="filters[col.key]"
                           @input="onFilterInput(col.key)"
                           @focus="showDropdown(col.key)"
                           @blur="hideDropdown(col.key)"
                           @keydown.enter="hideDropdown(col.key)"
                           type="text"
                           class="border rounded px-2 py-1 w-full text-xs"
                           placeholder="A, B für ODER" />

                    <div v-if="activeDropdown === col.key && filteredFieldOptions(col.key).length > 0"
                         class="absolute z-[100] bg-white border rounded shadow-lg mt-1 max-h-48 overflow-y-auto w-full min-w-[150px]">
                      <div v-for="opt in filteredFieldOptions(col.key)"
                           :key="opt.value"
                           @mousedown.prevent="addToCommaList(col.key, opt.value); hideDropdown(col.key)"
                           class="px-2 py-1 hover:bg-blue-50 cursor-pointer text-sm text-left">
                        {{ opt.label }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filteredRows" :key="row.id" class="border-t">
            <td class="p-2 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-100">
              <button class="text-primary hover:underline" @click="edit(row.id)" :title="'Bearbeiten'">
                <font-awesome-icon icon="pen" />
              </button>
              <button v-if="['Contracts','Customers','Contacts','Polls','Projects'].includes(type)" class="text-purple-600 hover:text-purple-800 hover:underline ml-2" @click="editNew(row.id)" :title="'Bearbeiten (modern)'">
                <font-awesome-icon icon="pen" />
              </button>
              <button
                v-if="canDeleteContracts"
                class="text-red-600 hover:text-red-800 hover:underline ml-2"
                @click="deleteContract(row)"
                title="Löschen"
                aria-label="Vertrag löschen"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </td>
            <td v-for="col in visibleColumnsList" :key="row.id + '-' + col.key" class="p-2 whitespace-nowrap">
              <template v-if="type==='Contracts' && col.key==='Klasse' && canEditKlasse">
                <div class="flex items-center gap-2">
                  <input
                    class="border rounded px-2 py-1 w-36"
                    :class="{ 'bg-green-50 border-green-400': savedFlash[row.id], 'opacity-60': savingKlasse[row.id] }"
                    :value="editedKlasse[row.id] !== undefined ? editedKlasse[row.id] : (row[col.key] || '')"
                    @input="onInputKlasse(row, $event.target.value)"
                    @keydown.ctrl.enter.prevent="saveKlasse(row)"
                  />
                </div>
                <div v-if="editedKlasse[row.id] !== undefined && editedKlasse[row.id] !== (row[col.key] || '')" class="text-xs text-gray-500 mt-1">
                  Speichern mit STRG+ENTER
                </div>
              </template>
              <template v-else-if="type==='Contracts' && isAbholzeitField(col.key) && canEditKlasse">
                <div class="flex items-center gap-2">
                  <input
                    type="time"
                    class="border rounded px-2 py-1 w-28"
                    :class="{ 'bg-green-50 border-green-400': savedFlashAbholzeit[row.id + '-' + col.key], 'opacity-60': savingAbholzeit[row.id + '-' + col.key] }"
                    :value="editedAbholzeit[row.id + '-' + col.key] !== undefined ? editedAbholzeit[row.id + '-' + col.key] : (formatTimeForInput(row[col.key]) || '')"
                    @input="onInputAbholzeit(row, col.key, $event.target.value)"
                    @keydown.ctrl.enter.prevent="saveAbholzeit(row, col.key)"
                  />
                </div>
                <div v-if="editedAbholzeit[row.id + '-' + col.key] !== undefined && editedAbholzeit[row.id + '-' + col.key] !== (formatTimeForInput(row[col.key]) || '')" class="text-xs text-gray-500 mt-1">
                  Speichern mit STRG+ENTER
                </div>
              </template>
              <template v-else>
                {{ formatCell(row[col.key], col) }}
              </template>
            </td>
          </tr>
          <tr v-if="loadingMore || loading" class="text-center text-gray-500">
            <td :colspan="visibleColumnsList.length + 1" class="p-4">Lade ...</td>
          </tr>
          <tr v-if="!loading && rows.length===0" class="text-center text-gray-500">
            <td :colspan="visibleColumnsList.length + 1" class="p-4">Keine Daten</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="type==='Contracts' && Object.keys(sums).length" class="mt-2 text-xs lg:text-sm text-gray-700">
      <div class="bg-white border rounded p-2 flex flex-wrap gap-2 lg:gap-4 overflow-x-auto">
        <div v-for="(val,key) in displaySums" :key="key" class="mr-4">
          <span class="font-semibold">{{ key }}:</span> {{ val }}
        </div>
      </div>
    </div>

    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-2 text-sm text-gray-600">
      <div>
        Seite {{ pagination.page }} / {{ pagination.total_pages }} · {{ pagination.total_count }} Einträge
      </div>
      <div class="flex gap-2 w-full sm:w-auto">
        <button class="px-2 py-1 border rounded flex-1 sm:flex-none text-sm lg:text-base" :disabled="!pagination.has_prev || loading" @click="prevPage">Zurück</button>
        <button class="px-2 py-1 border rounded flex-1 sm:flex-none text-sm lg:text-base" :disabled="!pagination.has_next || loading" @click="nextPage">Weiter</button>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/API'

function debounce(fn, delay){
  let t
  return function(...args){
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), delay)
  }
}

export default {
  name: 'DataTable',
  props: {
    type: { type: String, default: 'Contracts' },
    user: { type: Object, required: true }
  },
  data(){
    return {
      title: this.type === 'Contracts' ? 'Relevante Verträge' : (this.type === 'Customers' ? 'Kunden' : (this.type === 'Projects' ? 'Projekte' : (this.type === 'Contacts' ? 'Kontakte' : (this.type === 'Polls' ? 'Umfragen' : (this.type === 'Employees' ? 'Mitarbeiter' : this.type))))),
      columns: [],
      defaultVisible: {},
      visible: {},
      rows: [],
      loading: true,
      loadingMore: false,
      pagination: { page: 1, page_size: 50, total_pages: 1, total_count: 0, has_next: false, has_prev: false },
      search: '',
      showAllContracts: false,
      sort_by: 'id',
      sort_order: 'desc',
      filters: { 
        Status: '', 
        ProjektTitel: '', 
        Klasse: '',
        CustomerStatus: '', 
        TargetDate: ''
      },
      localFilters: {},
      operators: {},
      dateFilters: {},
      statusOptions: [],
      customerStatusOptions: [],
      projectOptions: [],
      projectSearch: '',
      showProjectDropdown: false,
      columnModalOpen: false,
      columnFilter: '',
      // Drag and drop state
      draggedItem: null,
      draggedFrom: null,
      draggedIndex: null,
      columnOrder: [],
      sums: {},
      // options for multi-select filters
      klasseOptions: [],
      // editing Klasse support
      editedKlasse: {},
      savingKlasse: {},
      savedFlash: {},
      // editing Abholzeiten support
      editedAbholzeit: {},
      savingAbholzeit: {},
      savedFlashAbholzeit: {},
      // CSV import for Contacts
      importModalOpen: false,
      importFile: null,
      importResult: null,
      importing: false,
      // Essensinfos modal
      essensinfosModalOpen: false,
      essensinfosData: {},
      // New state for generalized dropdowns
      fieldOptions: {},
      activeDropdown: null,
      aktionenOpen: false,
    }
  },
  computed: {
    baseOrigin(){
      try{
        const envUrl = process.env.VUE_APP_INTERNAL_URL
        if(envUrl){
          return new URL(envUrl).origin
        }
      }catch(e){}
      return new URL(window.location.href).origin
    },
    canEditKlasse(){
      // Allow editing only with full edit rights (align with legacy: >2)
      return this.type==='Contracts' && this.user && (this.user.Contracts > 2 || this.user.restrictedViews)
    },
    canDeleteContracts(){
      return this.type === 'Contracts' && this.user && Number(this.user.Contracts) >= 4
    },
    isAbholzeitField(){
      return (key) => {
        const abholzeitFields = ['MontagAbholzeit', 'DienstagAbholzeit', 'MittwochAbholzeit', 'DonnerstagAbholzeit', 'FreitagAbholzeit']
        return abholzeitFields.includes(key)
      }
    },
    visibilityKey(){
      return `datatable_visibility_${this.type}`
    },
    filtersKey(){
      return `datatable_filters_${this.type}`
    },
    dateFiltersKey(){
      return `datatable_datefilters_${this.type}`
    },
    operatorsKey(){
      return `datatable_operators_${this.type}`
    },
    legacyProjectsOverviewUrl(){
      return `${this.baseOrigin}/intern/projects/overview`
    },
    legacyListUrl(){
      // Build legacy list URL with current filters for Contracts
      if(this.type !== 'Contracts') return '#'
      const p = { ...this.params() }
      // remove frontend-only params
      delete p.type; delete p.page; delete p.page_size; delete p.sort_by; delete p.sort_order
      // create base URL safely using origin only
      const url = new URL(`/intern/contracts/`, this.baseOrigin)
      // append params explicitly
      Object.keys(p).forEach(k => {
        const val = p[k]
        if(val !== '' && val !== null && val !== undefined) {
          url.searchParams.append(k, val)
        }
      })
      return url.toString()
    },
    filteredProjectOptions() {
      if (!this.projectSearch) {
        return this.projectOptions
      }
      const searchLower = this.projectSearch.toLowerCase()
      return this.projectOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchLower)
      )
    },
    visibleCount(){
      return Object.values(this.visible).filter(Boolean).length
    },
    displaySums(){
      // Return sums with nicer labels where applicable, but only for visible columns
      if(this.type !== 'Contracts' || !this.sums) return {}
      
      // Get visible column keys
      const visibleKeys = this.visibleColumns.map(col => col.key)
      
      const labels = {
        Montag: 'Mo', Dienstag: 'Di', Mittwoch: 'Mi', Donnerstag: 'Do', Freitag: 'Fr',
        Vegetarisch: 'Vegetarisch', Laktosefrei: 'Laktosefrei', Zoeliakie: 'Zöliakie',
        Nussallergie: 'Nussallergie', Schweinefleischfrei: 'Ohne Schwein', Sonderessen: 'Sonderessen',
        Selbstzahler: 'Selbstzahler', BUTBerechtigt: 'BUT berechtigt', Kosten: 'Kosten (Summe)'
      }
      
      const out = {}
      Object.entries(this.sums).forEach(([k,v]) => {
        // Only include sums for columns that are currently visible
        if(visibleKeys.includes(k)){
          out[labels[k] || k] = v
        }
      })
      return out
    },
    visibleColumnsList(){
      // Return visible columns in the current order
      const orderedCols = []
      if (this.columnOrder.length > 0) {
        // Use custom order if defined
        this.columnOrder.forEach(key => {
          const col = this.columns.find(c => c.key === key)
          if (col && this.visible[col.key]) {
            orderedCols.push(col)
          }
        })
        // Add any visible columns not in order
        this.columns.forEach(col => {
          if (this.visible[col.key] && !orderedCols.find(c => c.key === col.key)) {
            orderedCols.push(col)
          }
        })
      } else {
        // Use default order
        this.columns.forEach(col => {
          if (this.visible[col.key]) {
            orderedCols.push(col)
          }
        })
      }
      return orderedCols
    },
    availableColumns(){
      return this.filteredColumns.filter(col => !this.visible[col.key])
    },
    visibleColumns(){
      return this.columns.filter(c => this.visible[c.key])
    },
    filteredColumns(){
      if(!this.columnFilter) return this.columns
      const filter = this.columnFilter.toLowerCase()
      return this.columns.filter(col => 
        col.label.toLowerCase().includes(filter) || 
        col.key.toLowerCase().includes(filter)
      )
    },
    hasActiveFilters(){
      // Check if search has content
      if (this.search && this.search.trim() !== '') return true
      
      // Check if any filters have content
      for (const [key, value] of Object.entries(this.filters)) {
        if (Array.isArray(value)) {
          if (value.length > 0) return true
        } else if (value && value.toString().trim() !== '') return true
      }
      
      // Check if any date filters have content
      for (const [key, value] of Object.entries(this.dateFilters)) {
        if (value && value.toString().trim() !== '') return true
      }
      
      return false
    },
    activeFilterChips(){
      // Summary of all currently applied filters (search, column filters, date filters)
      const chips = []
      if (this.search && this.search.trim() !== '') {
        chips.push({ kind: 'search', key: 'search', label: 'Suche', display: this.search })
      }
      const specialLabels = { CustomerStatus: 'Status', TargetDate: 'Stichtag', Projekt: 'Projekt' }
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return
        const col = this.columns.find(c => c.key === key)
        const label = (col && col.label) || specialLabels[key] || key
        let display = String(value)
        if (key === 'Projekt') {
          display = this.getProjectLabel(value) || String(value)
        } else if (col && col.type === 'boolean') {
          display = (value === 'True' || value === true) ? 'Ja' : 'Nein'
        }
        chips.push({ kind: 'filter', key, label, display })
      })
      const opSymbols = { eq: '=', gt: '>', lt: '<', gte: '≥', lte: '≤' }
      Object.entries(this.dateFilters).forEach(([key, value]) => {
        if (!value) return
        const col = this.columns.find(c => c.key === key)
        const op = opSymbols[this.operators[key]] || '='
        chips.push({ kind: 'date', key, label: col ? col.label : key, display: `${op} ${this.formatChipDate(value)}` })
      })
      return chips
    },
    filteredRows(){
      // Apply light client-side column-specific filters for non-server fields
      if(!this.localFilters || Object.keys(this.localFilters).length===0) return this.rows
      return this.rows.filter(r =>{
        return Object.entries(this.localFilters).every(([k,v]) =>{
          if(!v) return true
          const val = (r[k] ?? '').toString().toLowerCase()
          return val.includes(v.toString().toLowerCase())
        })
      })
    }
  },
  mounted(){
    // Save current URL to cookie for back navigation
    document.cookie = "tableReferrer=" + window.location.href + ";path=/";
    
    // initialize edited values once table first renders
    if (typeof this.initEditedKlasse === 'function') {
      this.initEditedKlasse()
    }
  },
  beforeDestroy(){
    // Clean up is handled by Vue's reactivity system
  },
  watch: {
    $route(to) {
      if (to.query) {
        if (to.query.search) {
          this.search = to.query.search
        }
        Object.keys(to.query).forEach(key => {
          if (key !== 'search') {
            this.filters[key] = to.query[key]
          }
        })
        delete this.filters.Archiviert
        this.reload()
      }
    },
    rows: {
      handler(){
        if (typeof this.initEditedKlasse === 'function') {
          this.initEditedKlasse()
        }
      },
      deep: true
    },
    visible: {
      handler(){
        this.persistVisibility()
      },
      deep: true
    },
    filters: {
      handler(){
        this.persistFilters()
      },
      deep: true
    },
    search(){
      this.persistFilters()
    },
    dateFilters: {
      handler(){ this.persistDateFilters() },
      deep: true
    },
    operators: {
      handler(){ this.persistDateFilters() },
      deep: true
    },
    columnOrder: {
      handler(){
        this.persistVisibility()
      },
      deep: true
    }
  },
  created(){
    this.debouncedReload = debounce(() => this.reload(), 350)
    this.init()
  },
  methods: {
    addNewEntry(){
      // Route to new Vue-based edit pages for supported types
      if(['Contracts', 'Customers', 'Contacts', 'Polls', 'Projects'].includes(this.type)) {
        this.$router.push(`/${this.type.toLowerCase()}/new`)
      } else {
        // Fallback to legacy Django template for other types
        const kind = this.type === 'Employees' ? 'employees' : this.type.toLowerCase()
        const url = new URL(`/intern/${kind}/edit`, this.baseOrigin)
        try { url.searchParams.set('redirectTo', window.location.href) } catch(e) {}
        window.location.href = url.toString()
      }
    },
    openColumnModal(){
      this.columnModalOpen = true
      // Initialize column order based on current visible columns if empty
      if (this.columnOrder.length === 0) {
        // Use all columns in their default order, filtering for visible ones
        this.columnOrder = this.columns
          .filter(col => this.visible[col.key])
          .map(col => col.key)
      } else {
        // Clean up column order to only include visible columns and remove duplicates
        const visibleKeys = new Set()
        this.columns.forEach(col => {
          if (this.visible[col.key]) visibleKeys.add(col.key)
        })
        
        const cleanOrder = []
        const seen = new Set()
        
        // Preserve existing order for visible columns
        this.columnOrder.forEach(key => {
          if (visibleKeys.has(key) && !seen.has(key)) {
            cleanOrder.push(key)
            seen.add(key)
          }
        })
        
        // Add any visible columns not in order (in their original column definition order)
        this.columns.forEach(col => {
          if (visibleKeys.has(col.key) && !seen.has(col.key)) {
            cleanOrder.push(col.key)
            seen.add(col.key)
          }
        })
        
        this.columnOrder = cleanOrder
      }
    },
    closeColumnModal(){
      this.columnModalOpen = false
    },
    handleDragStart(item, from, index = null){
      this.draggedItem = item
      this.draggedFrom = from
      this.draggedIndex = index
    },
    handleDrop(targetItem, targetLocation, targetIndex = null){
      if (!this.draggedItem) {
        return
      }
      
      // Don't do anything if dropping on the same item
      if (this.draggedItem.key === targetItem.key) {
        return
      }
      
      if (this.draggedFrom === 'available' && targetLocation === 'visible') {
        // Moving from available to visible - make it visible
        this.toggleColumn(this.draggedItem.key, true)
        // Add to column order at the target position, but only if not already present
        const newOrder = [...this.columnOrder]
        // Remove any existing entry first to prevent duplicates
        const existingIndex = newOrder.indexOf(this.draggedItem.key)
        if (existingIndex > -1) {
          newOrder.splice(existingIndex, 1)
        }
        // Add at the target position
        newOrder.splice(targetIndex || newOrder.length, 0, this.draggedItem.key)
        this.columnOrder = newOrder
      } else if (this.draggedFrom === 'visible' && targetLocation === 'available') {
        // Moving from visible to available - hide it
        this.toggleColumn(this.draggedItem.key, false)
        // Remove from column order
        this.columnOrder = this.columnOrder.filter(key => key !== this.draggedItem.key)
      } else if (this.draggedFrom === 'visible' && targetLocation === 'visible') {
        // Reordering within visible columns
        const newOrder = [...this.columnOrder]
        const draggedKey = this.draggedItem.key
        const targetKey = targetItem.key
        
        // Remove dragged item from its current position
        const draggedPos = newOrder.indexOf(draggedKey)
        if (draggedPos > -1) {
          newOrder.splice(draggedPos, 1)
        }
        
        // Insert at target position
        const targetPos = newOrder.indexOf(targetKey)
        if (targetPos > -1) {
          newOrder.splice(targetPos, 0, draggedKey)
        } else {
          newOrder.push(draggedKey)
        }
        
        this.columnOrder = newOrder
      }
      
      // Reset drag state
      this.draggedItem = null
      this.draggedFrom = null
      this.draggedIndex = null
    },
    toggleSort(key){
      if(this.sort_by === key){
        this.sort_order = this.sort_order === 'asc' ? 'desc' : 'asc'
      }else{
        this.sort_by = key
        this.sort_order = 'asc'
      }
      this.reload()
    },
    getSortIcon(key){
      if(this.sort_by !== key) return 'sort'
      return this.sort_order === 'asc' ? 'sort-up' : 'sort-down'
    },
    onInputKlasse(row, val){
      if(this.editedKlasse[row.id] === undefined){
        this.editedKlasse[row.id] = row.Klasse || ''
      }
      this.editedKlasse[row.id] = val
    },
    onInputAbholzeit(row, field, val){
      const key = row.id + '-' + field
      if(this.editedAbholzeit[key] === undefined){
        this.editedAbholzeit[key] = this.formatTimeForInput(row[field]) || ''
      }
      this.editedAbholzeit[key] = val
    },
    formatTimeForInput(timeValue){
      if(!timeValue) return ''
      if(typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}/)){
        return timeValue.slice(0, 5) // Return HH:MM format
      }
      return ''
    },
    initEditedKlasse(){
      if(this.type !== 'Contracts') return
      if(!Array.isArray(this.rows)) return
      this.rows.forEach(r => {
        if(this.editedKlasse[r.id] === undefined){
          this.editedKlasse[r.id] = r.Klasse || ''
        }
        // Initialize Abholzeiten
        const abholzeitFields = ['MontagAbholzeit', 'DienstagAbholzeit', 'MittwochAbholzeit', 'DonnerstagAbholzeit', 'FreitagAbholzeit']
        abholzeitFields.forEach(field => {
          const key = r.id + '-' + field
          if(this.editedAbholzeit[key] === undefined){
            this.editedAbholzeit[key] = this.formatTimeForInput(r[field]) || ''
          }
        })
      })
    },
    saveKlasse(row){
      this.saveContractField(row, 'Klasse', this.editedKlasse[row.id], 'savedFlash', 'savingKlasse')
    },
    saveAbholzeit(row, field){
      const key = row.id + '-' + field
      const newVal = this.editedAbholzeit[key] ?? ''
      this.saveContractField(row, field, newVal, 'savedFlashAbholzeit', 'savingAbholzeit', key)
    },
    saveContractField(row, fieldName, newValue, flashStateKey, savingStateKey, stateKey = null){
      if(this.type !== 'Contracts') return
      if(!this.canEditKlasse){
        if(this.$toast){ this.$toast.open({ message: 'Keine Berechtigung zum Bearbeiten', type: 'error', position: 'top' }) }
        return
      }
      
      // Use provided stateKey or fallback to row.id for simple fields
      const key = stateKey || row.id
      
      // Check if value actually changed
      const currentValue = fieldName.includes('Abholzeit') ? this.formatTimeForInput(row[fieldName]) || '' : row[fieldName] || ''
      if(newValue === currentValue) return
      
      // Set saving state
      this[savingStateKey][key] = true
      
      API.updateContractField(row.id, fieldName, newValue, (resp, status)=>{
        this[savingStateKey][key] = false
        if(status===200){
          // Update row data
          row[fieldName] = newValue
          // Set flash state
          this[flashStateKey][key] = true
          setTimeout(()=>{
            this[flashStateKey][key] = false
          }, 1200)
          if(this.$toast){ this.$toast.open({ message: `${fieldName} gespeichert`, type: 'success', position: 'top' }) }
        }else if(status===401 || status===403){
          if(this.$toast){ this.$toast.open({ message: 'Keine Berechtigung zum Bearbeiten', type: 'error', position: 'top' }) }
        }else{
          const errorMsg = resp?.error || 'Speichern fehlgeschlagen'
          if(this.$toast){ this.$toast.open({ message: errorMsg, type: 'error', position: 'top' }) }
        }
      })
    },
    async init(){
      // load persisted filters BEFORE loading columns to avoid watcher overwriting them
      this.loadPersistedFilters()
      this.loadPersistedDateFilters()
      await this.loadColumns()
      await this.loadFilterOptions()
      // load persisted visibility after columns are loaded
      this.loadPersistedVisibility()
      // apply query param filters (e.g. ?Projekt=123 from ProjectEdit, ?Status=Eingegangen from Dashboard)
      if (this.$route?.query) {
        if (this.$route.query.search) {
          this.search = this.$route.query.search
        }
        Object.keys(this.$route.query).forEach(key => {
          if (key !== 'search') {
            this.filters[key] = this.$route.query[key]
          }
        })
      }
      delete this.filters.Archiviert
      await this.reload()
    },
    async loadColumns(){
      return new Promise((resolve)=>{
        API.getTableColumns(this.type, (res, status)=>{
          if(status===200){
            this.columns = res.columns
            // default visibility depends on table type
            const defaultsByType = {
              Contracts: ['id','Vorname','Nachname','ProjektTitel','Status','Vertragsbeginn','Vertragsende','KundeNachname','KundeVorname','KundeEmail'],
              Customers: ['id','NachnameErziehungsberechtigter','VornameErziehungsberechtigter','EmailEltern','Status','Ort','createdAt'],
              Projects: ['id','Titel','Ort','Projektart','StartDatum','EndDatum','Archiviert'],
              Employees: ['id','Vorname','Nachname','Funktion','Email','Festnetz','Mobilnummer','Aktiv']
            }
            const defaultKeys = defaultsByType[this.type] || []
            // try load visibility from localStorage
            const persisted = localStorage.getItem(this.visibilityKey)
            let persistedObj = null
            try { persistedObj = persisted ? JSON.parse(persisted) : null } catch(e){ persistedObj = null }
            this.columns.forEach(c => {
              this.defaultVisible[c.key] = defaultKeys.includes(c.key) || this.columns.indexOf(c) < 8
              this.visible[c.key] = (persistedObj && persistedObj.visible && c.key in persistedObj.visible) ? persistedObj.visible[c.key] : this.defaultVisible[c.key]
              if(c.type==='date'){
                // Don't overwrite values restored from persisted date filters
                if (!(c.key in this.operators)) this.operators[c.key] = 'eq'
                if (!(c.key in this.dateFilters)) this.dateFilters[c.key] = ''
              }
              // Initialize filter state
              const fKey = c.key
              if (!(fKey in this.filters)) {
                this.filters[fKey] = ''
              }
            })
            // Load column order if available
            if (persistedObj && persistedObj.order && Array.isArray(persistedObj.order)) {
              // Filter to only include keys that exist in columns and are visible
              const validKeys = new Set(this.columns.map(c => c.key))
              this.columnOrder = persistedObj.order.filter(key => validKeys.has(key) && this.visible[key])
            } else {
              // Initialize with visible columns in their default order
              this.columnOrder = this.columns
                .filter(c => this.visible[c.key])
                .map(c => c.key)
            }
          }
          resolve()
        })
      })
    },
    async loadFilterOptions(){
      if(this.type==='Contracts'){
        API.getFilterOptions('Contracts','Status',(res, status)=>{
          if(status===200) this.statusOptions = res.options.filter(o=>o.value)
        })
        API.getFilterOptions('Contracts','Projekt',(res, status)=>{
          if(status===200) this.projectOptions = res.options.filter(o=>o.value)
        })
        API.getFilterOptions('Contracts','Klasse',(res, status)=>{
          if(status===200) this.klasseOptions = res.options.filter(o=>o.value)
        })
      }
      if(this.type==='Customers'){
        API.getFilterOptions('Customers','Status',(res, status)=>{
          if(status===200) this.customerStatusOptions = res.options.filter(o=>o.value)
        })
      }
    },
    params(){
      const p = {
        type: this.type,
        page: this.pagination.page,
        page_size: this.pagination.page_size,
        search: this.search,
        sort_by: this.sort_by,
        sort_order: this.sort_order
      }
      if(this.type === 'Contracts' && this.showAllContracts) {
        p.includeAll = 'true'
      }
      
      // Handle specific filters based on table type
      if(this.type === 'Contracts') {
        if(this.filters.TargetDate) p.TargetDate = this.filters.TargetDate
      } else if(this.type === 'Customers') {
        if(this.filters.CustomerStatus) p.Status = this.filters.CustomerStatus
      }
      
      // Add all other filters from the filters object
      Object.entries(this.filters).forEach(([key, value]) => {
        if(value && value !== '' && !['CustomerStatus', 'TargetDate'].includes(key)) {
          p[key] = value
        }
      })
      
      // boolean column filters (from column definitions)
      this.columns.forEach(c => {
        if(c.type==='boolean' && this.filters[c.key] !== undefined && this.filters[c.key] !== ''){
          p[c.key] = this.filters[c.key]
        }
      })
      
      // date column operators
      Object.entries(this.dateFilters).forEach(([k,v])=>{
        if(v){
          p[k] = v
          p[`operator_${k}`] = this.operators[k] || 'eq'
        }
      })
      
      return p
    },
    async reload(){
      this.loading = true
      this.pagination.page = 1
      const params = this.params()
      console.log('DataTable reload params:', params)
      API.getTableData(params, (res, status)=>{
        this.loading = false
        if(status===200){
          this.rows = res.data
          this.pagination = res.pagination
          this.fetchSums()
          this.$nextTick(()=> {
            if (typeof this.initEditedKlasse === 'function') {
              this.initEditedKlasse()
            }
          })
        } else {
          console.error('API getTableData failed:', status, res)
          // Show error to user if toast is available
          if(this.$toast) {
            this.$toast.open({ 
              message: `Laden fehlgeschlagen (${status})`, 
              type: 'error', 
              position: 'top' 
            })
          }
        }
      })
    },
    loadMore(){
      if(!this.pagination.has_next || this.loadingMore) return
      this.loadingMore = true
      const p = { ...this.params(), page: this.pagination.page + 1 }
      API.getTableData(p, (res, status)=>{
        this.loadingMore = false
        if(status===200){
          this.rows = this.rows.concat(res.data)
          this.pagination = res.pagination
        }
      })
    },
    fetchSums(){
      if(this.type !== 'Contracts') return
      API.getTableSums(this.params(), (res, status)=>{
        if(status===200) this.sums = res.sums || {}
      })
    },
    onScroll(e){
      const el = this.$refs.scrollContainer
      if(!el) return
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200
      if(nearBottom) this.loadMore()
    },
    showAllCols(v){
      Object.keys(this.visible).forEach(k => this.visible[k] = v)
      this.persistVisibility()
    },
    resetCols(){
      Object.entries(this.defaultVisible).forEach(([k,v]) => this.visible[k]=v)
      this.persistVisibility()
    },
    toggleColumn(key, val){
      this.visible[key] = val
      // Clean up column order to only include visible columns and remove duplicates
      if (val) {
        // Adding column - ensure it's in order array only once
        if (!this.columnOrder.includes(key)) {
          this.columnOrder.push(key)
        }
      } else {
        // Removing column - remove from order array
        this.columnOrder = this.columnOrder.filter(k => k !== key)
      }
      this.persistVisibility()
    },
    applyLocalFilters(){
      // just triggers computed
    },
    formatCell(value, col){
      if(value==null) return ''
      if((col.type==='date' || col.type==='datetime') && typeof value === 'string'){
        // Expect ISO strings from API. Convert to dd.mm.yyyy (and time if datetime)
        const d = new Date(value)
        if(!isNaN(d.getTime())){
          const dd = String(d.getDate()).padStart(2,'0')
          const mm = String(d.getMonth()+1).padStart(2,'0')
          const yyyy = d.getFullYear()
          const dateStr = `${dd}.${mm}.${yyyy}`
          if(col.type==='datetime'){
            const hh = String(d.getHours()).padStart(2,'0')
            const min = String(d.getMinutes()).padStart(2,'0')
            return `${dateStr} ${hh}:${min}`
          }
          return dateStr
        }
        // Fallback: format simple YYYY-MM-DD if string matches
        if(col.type==='date' && /^\d{4}-\d{2}-\d{2}/.test(value)){
          const [y,m,rest] = value.split('-')
          const d2 = rest.slice(0,2)
          return `${d2}.${m}.${y}`
        }
        if(col.type==='datetime'){
          const [datePart,timePart] = value.replace('T',' ').split(' ')
          if(datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)){
            const [y,m,d3] = datePart.split('-')
            return `${d3}.${m}.${y} ${timePart?.slice(0,5) || ''}`.trim()
          }
        }
      }
      if(col.type==='boolean') return value ? 'Ja' : 'Nein'
      if(col.type==='time' && value){
        if(typeof value === 'string' && value.match(/^\d{2}:\d{2}/)){
          return value.slice(0, 5) // Return HH:MM format
        }
      }
      return value
    },
    edit(id){
      const kind = this.type === 'Contracts' ? 'contracts' : this.type.toLowerCase()
      const url = new URL(`/intern/${kind}/edit`, this.baseOrigin)
      url.searchParams.set('id', String(id))
      // pass current SPA location so legacy edit can return here
      try {
        url.searchParams.set('redirectTo', window.location.href)
      } catch(e) {}
      window.location = url.toString()
    },
    editNew(id){
      // Open Vue contract editor in modal or new view
      // For now, we'll use a simple approach - navigate to a new route
    
      const url = new URL(`${this.type.toLowerCase()}/${id}`, window.location.origin)
      window.location = url.toString()
    },
    deleteContract(row){
      if(!this.canDeleteContracts || !confirm('Soll dieser Vertrag wirklich gelöscht werden?')) return

      API.deleteContract(row.id, (res, status) => {
        if(status === 200){
          this.reload()
          this.$toast?.open?.({
            message: 'Vertrag gelöscht',
            type: 'success',
            position: 'top'
          })
        } else {
          this.$toast?.open?.({
            message: res?.error || 'Vertrag konnte nicht gelöscht werden',
            type: 'error',
            position: 'top'
          })
        }
      })
    },
    exportCsv(){
      const p = this.params()
      p.visible = this.visibleColumnsList.map(c => c.key).join(',')
      API.exportTableCsv(p)
    },
    persistVisibility(){
      const visibilityData = {
        visible: this.visible,
        order: this.columnOrder
      }
      localStorage.setItem(this.visibilityKey, JSON.stringify(visibilityData))
    },
    persistFilters(){
      const obj = { filters: this.filters, search: this.search }
      localStorage.setItem(this.filtersKey, JSON.stringify(obj))
    },
    loadPersistedFilters(){
      try{
        const raw = localStorage.getItem(this.filtersKey)
        if(!raw) return
        const obj = JSON.parse(raw)
        if (obj && obj.filters) { 
          // All filters are now unified as strings
          Object.keys(obj.filters).forEach(k => {
            const val = obj.filters[k]
            if (Array.isArray(val)) {
              obj.filters[k] = val.join(',')
            }
          })
          Object.assign(this.filters, obj.filters) 
        }
        if(typeof obj.search === 'string'){ this.search = obj.search }
      }catch(e){}
    },
    persistDateFilters(){
      const obj = { dateFilters: this.dateFilters, operators: this.operators }
      localStorage.setItem(this.dateFiltersKey, JSON.stringify(obj))
    },
    loadPersistedDateFilters(){
      try{
        const raw = localStorage.getItem(this.dateFiltersKey)
        if(!raw) return
        const obj = JSON.parse(raw)
        if(obj && obj.dateFilters){ this.dateFilters = Object.assign(this.dateFilters, obj.dateFilters) }
        if(obj && obj.operators){ this.operators = Object.assign(this.operators, obj.operators) }
      }catch(e){}
    },
    loadPersistedVisibility(){
      try{
        const persisted = localStorage.getItem(this.visibilityKey)
        if(!persisted) return
        const persistedObj = JSON.parse(persisted)
        if(persistedObj && persistedObj.visible){
          Object.assign(this.visible, persistedObj.visible)
        }
        if(persistedObj && persistedObj.order && Array.isArray(persistedObj.order)){
          this.columnOrder = persistedObj.order.filter(key => this.columns.find(c => c.key === key))
        }
      }catch(e){}
    },
    showAllContractFilter(){
      if(this.type !== 'Contracts') return
      this.showAllContracts = true
      this.reload()
    },
    clearAllFilters(){
      // Clear search
      this.search = ''
      
      // Clear all filters
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = ''
      })
      
      // Clear all date filters
      Object.keys(this.dateFilters).forEach(key => {
        this.dateFilters[key] = ''
      })
      
      // Reset operators to default
      Object.keys(this.operators).forEach(key => {
        this.operators[key] = 'eq'
      })

      // Reload data
      this.reload()
    },
    sendInfomail(type) {
      this.sendMailLink(type, true)
    },
    sendMail(type) {
      this.sendMailLink(type, false)
    },
    sendMailLink(type, respectInfomails) {
      const params = this.params()
      API.getContractEmails(params, (data, status) => {
        if (status === 200 && data.emails) {
          const emails = new Set()
          data.emails.forEach(e => {
            // type 1 = EmailEltern, type 2 = EmailEltern2, type 3 = both
            if ((type === 1 || type === 3) && e.EmailEltern) {
              if (!respectInfomails || !e.KeineInfomails) {
                emails.add(e.EmailEltern)
              }
            }
            if ((type === 2 || type === 3) && e.EmailEltern2) {
              if (!respectInfomails || !e.KeineInfomails) {
                emails.add(e.EmailEltern2)
              }
            }
          })
          
          if (emails.size === 0) {
            this.$toast?.open?.({
              message: 'Keine E-Mail-Adressen gefunden',
              type: 'info',
              position: 'top'
            })
            return
          }
          
          const contactMail = process.env.VUE_APP_CONTACT_MAIL || 'kundensupport@ev-angel-isch.de'
          const bcc = Array.from(emails).join(',')
          const mailto = `mailto:${contactMail}?bcc=${encodeURIComponent(bcc)}`
          window.open(mailto, '_blank')
        }
      })
    },
    showEssensinfos() {
      const params = this.params()
      API.getEssensinfos(params, (data, status) => {
        if (status === 200 && data.combinations) {
          this.essensinfosData = data.combinations
          this.essensinfosModalOpen = true
        }
      })
    },
    closeEssensinfosModal() {
      this.essensinfosModalOpen = false
      this.essensinfosData = {}
    },
    getProjectLabel(projectId) {
      const project = this.projectOptions.find(opt => opt.value === projectId)
      return project ? project.label : ''
    },
    clearProject() {
      this.filters.Projekt = ''
      this.projectSearch = ''
      this.reload()
    },
    selectProject(projectId) {
      this.filters.Projekt = projectId
      this.projectSearch = this.getProjectLabel(projectId)
      this.showProjectDropdown = false
      this.reload()
    },
    selectFirstProject() {
      if (this.filteredProjectOptions.length > 0) {
        this.selectProject(this.filteredProjectOptions[0].value)
      }
    },
    hideProjectDropdown() {
      setTimeout(() => {
        this.showProjectDropdown = false
      }, 200)
    },
    clearColumnFilter(){
      this.columnFilter = ''
    },
    removeFilterChip(chip){
      if (chip.kind === 'search') {
        this.search = ''
        this.reload()
        return
      }
      if (chip.key === 'Projekt') {
        this.clearProject()
        return
      }
      this.clearFilter(chip.key)
    },
    formatChipDate(value){
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
      if (match) return `${match[3]}.${match[2]}.${match[1]}`
      return value
    },
    hasFilter(key){
      if (this.dateFilters[key]) return true
      const val = this.filters[key]
      return val && val !== ''
    },
    clearFilter(key){
      if (this.dateFilters[key]) {
        this.dateFilters[key] = ''
      }
      if (this.filters[key] !== undefined) {
        this.filters[key] = ''
      }
      this.activeDropdown = null
      this.reload()
    },
    getOptions(key){
      if (key === 'Status') return this.statusOptions
      if (key === 'ProjektTitel') return this.projectOptions
      if (key === 'Klasse') return this.klasseOptions
      return []
    },
    filteredFieldOptions(key){
      const options = this.fieldOptions[key] || []
      const currentVal = this.filters[key] || ''
      const parts = currentVal.split(',').map(p => p.trim())
      const lastPart = parts[parts.length - 1].toLowerCase()
      
      if(!lastPart) return options.slice(0, 50) // Limit to 50 for performance
      
      return options.filter(o => o.label.toLowerCase().includes(lastPart)).slice(0, 50)
    },
    showDropdown(key){
      this.activeDropdown = key
      if(!this.fieldOptions[key]){
        this.fetchOptionsForField(key)
      }
    },
    hideDropdown(key){
      if(this.activeDropdown === key) {
        this.activeDropdown = null
      }
    },
    fetchOptionsForField(key){
      // Map frontend key to backend field if needed
      const fieldName = key === 'ProjektTitel' ? 'ProjektTitel' : key
      API.getFilterOptions(this.type, fieldName, (res, status)=>{
        if(status===200){
          this.fieldOptions[key] = res.options
        }
      })
    },
    onFilterInput(key){
      this.debouncedReload()
      this.showDropdown(key)
      if(!this.fieldOptions[key]){
        this.fetchOptionsForField(key)
      }
    },
    addToCommaList(key, value){
      let currentVal = this.filters[key] || ''
      let uniqueParts = []
      
      if(!currentVal) {
        uniqueParts = [value]
      } else {
        let parts = currentVal.split(',').map(p => p.trim())
        // Replace the last part (which the user was typing) with the selected value
        if(parts.length > 0) {
          parts[parts.length - 1] = value
        } else {
          parts.push(value)
        }
        
        // Remove duplicates and empty parts
        const seen = new Set()
        parts.forEach(p => {
          if(p && !seen.has(p.toLowerCase())) {
            uniqueParts.push(p)
            seen.add(p.toLowerCase())
          }
        })
      }
      
      // Join with comma and add a trailing comma+space for next selection
      this.filters[key] = uniqueParts.join(',') + ','
      this.reload()
    },
    openImportModal(){
      this.importModalOpen = true
      this.importFile = null
      this.importResult = null
    },
    closeImportModal(){
      this.importModalOpen = false
      this.importFile = null
      this.importResult = null
    },
    onFileSelected(event){
      const file = event.target.files[0]
      if(file){
        this.importFile = file
        this.importResult = null
      }
    },
    performImport(){
      if(!this.importFile || this.importing) return
      
      this.importing = true
      this.importResult = null
      
      API.importContactsCsv(this.importFile, (res, status) => {
        this.importing = false
        
        if(status === 200 && res){
          this.importResult = {
            success: true,
            created: res.created || 0,
            updated: res.updated || 0,
            errors: res.errors || []
          }
          // Reload table data after successful import
          this.reload()
        } else {
          this.importResult = {
            success: false,
            error: res?.error || 'Import fehlgeschlagen'
          }
        }
      })
    }
  }
}
</script>

<style scoped>
@media (max-width: 1023px) {
  input[type="date"]::-webkit-calendar-picker-indicator,
  input[type="time"]::-webkit-calendar-picker-indicator {
    display: none;
  }
  input[type="date"],
  input[type="time"] {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    padding-left: 4px;
    padding-right: 4px;
  }
}
</style>
